import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EmptyState, SkeletonLoader, StatusChip } from '@shared/design-system/components';
import { useAuth } from '@shared/auth/useAuth';
import {
  cancelSupportRunbookRun,
  getSupportRunbookRun,
  listRunbookRuns,
  listSupportRunbooks,
  listSupportSites,
  recordRunbookOutcome,
  runSupportRunbook,
  SupportApiError,
  type PastRun,
  type RunbookRunResponse,
  type SupportRunbookSummary,
} from '../../api/supportClient';
import { RetireOrphanedTask } from './RetireOrphanedTask';
import { EndHeldTask } from './EndHeldTask';
import { ORPHANED_TASK_RUNBOOK_ID } from './retire-task-rules';

export function stepStatusToStatusKind(status: string): 'active' | 'warning' | 'error' | 'offline' {
  if (status === 'succeeded') return 'active';
  if (status === 'failed') return 'error';
  if (status === 'needs_manual_review') return 'warning';
  if (status === 'running') return 'warning';
  // The ordered recovery's two transient states. The worker answered
  // `accepted` for a sequence that restarts the worker itself last, so
  // nothing can confirm it except watching the site come back --
  // amber, because it is genuinely neither done nor failed yet.
  if (status === 'accepted_unconfirmed') return 'warning';
  if (status === 'verifying') return 'warning';
  // THE ORDERED RECOVERY'S HONEST THIRD ANSWER. The broker was asked,
  // the site is healthy, and no restart was observable from the
  // platform tier -- which is exactly what a refused POST looks like
  // from here, because the wedge this runbook treats leaves every pod
  // Running and Ready. Amber, not green and not red: the engineer has
  // a question to answer, not a verdict to accept.
  if (status === 'unconfirmed') return 'warning';
  return 'offline'; // pending / skipped_cancelled
}

function OutcomeRecorder({ runId, hasSignature }: { runId: string; hasSignature: boolean }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [recorded, setRecorded] = useState<'resolved' | 'unresolved' | null>(null);

  const mutation = useMutation({
    mutationFn: (resolved: boolean) => recordRunbookOutcome(runId, { resolved, note }),
    onSuccess: () => {
      // The outcome creates a pending lesson and moves statistics --
      // both live in the Knowledge pane -- and clears this run from
      // the awaiting-outcome queue.
      void qc.invalidateQueries({ queryKey: ['support', 'lessons'] });
      void qc.invalidateQueries({ queryKey: ['support', 'knowledge-preview'] });
      void qc.invalidateQueries({ queryKey: ['support', 'runbook-runs'] });
    },
  });

  if (!hasSignature) {
    // The backend rejects an outcome with no failure_signature (409 --
    // an outcome unattributable to a signature teaches the assistant
    // nothing). Explained here rather than letting the operator hit
    // that 409 cold.
    return (
      <p style={{ marginTop: 10, fontSize: 12, color: 'var(--nx-muted)' }}>
        This runbook has no trigger signature, so its outcome cannot be recorded against the
        AI's knowledge base.
      </p>
    );
  }

  if (recorded) {
    return (
      <p style={{ marginTop: 10, fontSize: 13, color: 'var(--nx-cyan)' }}>
        Outcome recorded ({recorded === 'resolved' ? 'resolved' : 'not resolved'}). A draft lesson is
        now pending platform_admin review in the Knowledge pane.
      </p>
    );
  }

  return (
    <div style={{ marginTop: 10, borderTop: '1px solid var(--nx-border)', paddingTop: 10 }}>
      <p style={{ fontSize: 13, marginBottom: 6 }}>
        Did this actually resolve the underlying issue? (A run can complete every step and still
        not fix anything.)
      </p>
      <textarea
        placeholder="Optional note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        style={{ width: '100%', marginBottom: 6 }}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate(true, { onSuccess: () => setRecorded('resolved') })}
        >
          Resolved
        </button>
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate(false, { onSuccess: () => setRecorded('unresolved') })}
        >
          Did not resolve
        </button>
      </div>
      {mutation.isError && (
        <div style={{ color: 'var(--nx-error)', fontSize: 12, marginTop: 6 }}>
          {String(mutation.error)}
        </div>
      )}
    </div>
  );
}

function RunProgress({
  runId,
  onCancel,
  hasSignature,
}: {
  runId: string;
  onCancel: () => void;
  hasSignature: boolean;
}) {
  const runQuery = useQuery({
    queryKey: ['support', 'runbook-run', runId],
    queryFn: () => getSupportRunbookRun(runId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? 1000 : false;
    },
  });

  const run = runQuery.data;
  if (!run) return <SkeletonLoader shape="card-block" />;

  const finished = run.status === 'done' || run.status === 'failed' || run.status === 'cancelled';

  return (
    <div style={{ border: '1px solid var(--nx-border)', borderRadius: 'var(--nx-r)', padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>
          Run {run.id} — {run.runbook_id}
        </strong>
        <StatusChip
          status={
            run.status === 'done'
              ? 'active'
              : run.status === 'failed'
                ? 'error'
                : run.status === 'cancelled'
                  ? 'offline'
                  : 'warning'
          }
          label={run.status}
        />
      </div>
      <ol style={{ marginTop: 12 }}>
        {run.steps.map((step) => (
          <li key={step.index} style={{ marginBottom: 8 }}>
            <StatusChip status={stepStatusToStatusKind(step.status)} label={step.status} />{' '}
            <span>
              {step.kind}
              {step.service ? ` (${step.service} → ${step.verb})` : ''}
              {step.operation ? ` (${step.operation})` : ''}
            </span>
            {step.description && (
              <div style={{ fontSize: 12, color: 'var(--nx-muted)' }}>{step.description}</div>
            )}
            {step.detail && <div style={{ fontSize: 12, color: 'var(--nx-error)' }}>{step.detail}</div>}
          </li>
        ))}
      </ol>
      {(run.status === 'pending' || run.status === 'running') && (
        <button type="button" onClick={onCancel}>
          Abort remaining steps
        </button>
      )}
      {finished && run.status !== 'cancelled' && (
        <OutcomeRecorder runId={run.id} hasSignature={hasSignature} />
      )}
    </div>
  );
}

function RunbookCard({
  runbook,
  site,
  siteLabel,
  availability,
  availabilityPending = false,
  fromIssue = '',
  fromSignature = '',
  pinned = false,
  layoutSettled = true,
  children,
}: {
  /** A runbook-specific control rendered at the foot of the card. Only
   *  `orphaned-task-retire` has one: the retire itself, which is a
   *  console control rather than a step the engine runs. */
  children?: ReactNode;
  runbook: SupportRunbookSummary;
  /** Chosen ONCE for the whole pane, not per card. */
  site: string;
  siteLabel: string;
  /** This runbook's entry from `listSupportRunbooks(site)`, i.e. the
   *  server's own verdict on whether the chosen site can run it.
   *  `undefined` when no site is chosen yet, or when the server is
   *  older than this console -- neither is a refusal. */
  availability?: SupportRunbookSummary;
  availabilityPending?: boolean;
  fromIssue?: string;
  /** The signature the ISSUE was derived to, handed over by Diagnose.
   *  Empty when Act was opened directly. */
  fromSignature?: string;
  /** This is the runbook Diagnose sent the engineer here to run
   *  (`?runbook=`): it is sorted to the top, scrolled to, and opens
   *  with its confirm form already showing. Arriving at the top of a
   *  catalogue to hunt for the name you just clicked was the gap. */
  pinned?: boolean;
  /** Whether everything ABOVE this card has finished resolving --
   *  see the scroll effect below. */
  layoutSettled?: boolean;
}) {
  const { roles } = useAuth();
  const [reason, setReason] = useState(fromIssue ? `Recovery for issue ${fromIssue}. ` : '');
  const [confirming, setConfirming] = useState(pinned);
  const [run, setRun] = useState<RunbookRunResponse | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Waits for the layout ABOVE this card to settle. The
    // awaiting-outcome queue is its own query and lands a beat after
    // the catalogue does; scrolling on mount put the pinned card in
    // view and then the queue appeared above it and pushed it straight
    // back off-screen -- the engineer arrived from Diagnose looking at
    // someone else's unresolved run instead of the card they clicked.
    if (!pinned || !layoutSettled) return;
    const el = cardRef.current;
    // Guarded: jsdom (and any non-browser host) has no
    // `scrollIntoView`, and this is a convenience, not a behaviour
    // anything depends on.
    if (el && typeof el.scrollIntoView === 'function') {
      // `nearest`, not `start`: by this point the card may already be
      // fully visible, and scrolling it to the top of the viewport
      // anyway is a jump with no purpose.
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [pinned, layoutSettled]);

  const canRunL3 = roles.includes('nexsoss:support_l3') || roles.includes('nexsoss:platform_admin');
  const tierBlocked = runbook.tier === 'l3' && !canRunL3;
  // `=== false` and not `!runnable`: an absent verdict means "not
  // asked" (no site chosen) or "older server", and neither is a
  // refusal. Reading undefined as a block would disable every button
  // against an api-server that predates this field.
  const notRunnableHere = availability?.runnable === false;
  const unavailableReason = availability?.unavailable_reason ?? null;

  const runMutation = useMutation({
    mutationFn: () =>
      runSupportRunbook(runbook.id, {
        site,
        reason,
        // THE INCIDENT'S SIGNATURE WHEN THERE IS ONE, the runbook's own
        // otherwise. This is what the eventual outcome recording is
        // keyed against, and it is sent explicitly rather than left to
        // the backend's fallback so the value driving the AI's learning
        // loop is visible right here.
        //
        // The runbook's `trigger_signature` alone is wrong exactly when
        // `also_triggered_by` made the run possible: a wedge presenting
        // as `robot_dropped` runs zenoh-routing-recovery and the
        // outcome lands under `adapter_down`, so the next
        // `robot_dropped` is told there is no history for this
        // signature and gets the same failed restart recommended again.
        failure_signature: fromSignature || (runbook.trigger_signature ?? undefined),
      }),
    onSuccess: (resp) => {
      setRun(resp);
      setConfirming(false);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelSupportRunbookRun(run!.id),
  });

  return (
    <div
      ref={cardRef}
      data-testid={`runbook-card-${runbook.id}`}
      data-pinned={pinned ? 'true' : undefined}
      style={{
        border: `1px solid ${pinned ? 'var(--nx-cyan)' : 'var(--nx-border)'}`,
        borderRadius: 'var(--nx-r)',
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <strong>{runbook.title}</strong>
        <span style={{ display: 'flex', gap: 6 }}>
          {pinned && <StatusChip status="active" label="suggested for this issue" />}
          {runbook.tier && <StatusChip status={runbook.tier === 'l3' ? 'warning' : 'active'} label={runbook.tier} />}
          {!runbook.available && <StatusChip status="offline" label="not available" />}
          {availabilityPending && <StatusChip status="idle" label="checking…" />}
          {notRunnableHere && <StatusChip status="offline" label="not runnable here" />}
        </span>
      </div>
      {notRunnableHere && unavailableReason && (
        <p
          data-testid={`runbook-unavailable-reason-${runbook.id}`}
          style={{ color: 'var(--nx-warning)', fontSize: 13, margin: '4px 0' }}
        >
          {unavailableReason}
        </p>
      )}
      {runbook.description && <p style={{ color: 'var(--nx-muted)', fontSize: 13 }}>{runbook.description}</p>}
      {runbook.trigger_signature && (
        <p style={{ fontSize: 12, color: 'var(--nx-muted)' }}>Trigger signature: {runbook.trigger_signature}</p>
      )}
      <ol style={{ fontSize: 13 }}>
        {runbook.steps.map((s, i) => (
          <li key={i}>
            {s.kind}
            {s.service ? ` — ${s.service} → ${s.verb}` : ''}
            {s.operation ? ` — ${s.operation}` : ''}
          </li>
        ))}
      </ol>

      {tierBlocked && (
        <p style={{ color: 'var(--nx-error)' }}>This runbook requires an L3 (or platform admin) role.</p>
      )}

      {runbook.available && !tierBlocked && !run && (
        <>
          {!confirming ? (
            <button type="button" onClick={() => setConfirming(true)}>
              Run this runbook
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {/* The site is chosen once for the pane, above. It is
                  repeated here as text rather than a second control so
                  the confirmation names what is about to be touched --
                  a runbook restarts containers at a named site, and
                  that name belongs in front of the person confirming
                  it. */}
              <p style={{ margin: 0, fontSize: 13 }}>
                Target site:{' '}
                <strong data-testid={`runbook-target-site-${runbook.id}`}>
                  {site ? siteLabel || site : 'none selected'}
                </strong>
              </p>
              <textarea
                placeholder="Reason (required) — why are you running this?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
              />
              {runMutation.isError && (
                <div style={{ color: 'var(--nx-error)' }}>
                  {runMutation.error instanceof SupportApiError
                    ? runMutation.error.message
                    : 'Failed to start run'}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  disabled={
                    !site ||
                    !reason.trim() ||
                    runMutation.isPending ||
                    notRunnableHere ||
                    availabilityPending
                  }
                  onClick={() => runMutation.mutate()}
                >
                  Confirm &amp; run
                </button>
                <button type="button" onClick={() => setConfirming(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {run && (
        <RunProgress
          runId={run.id}
          onCancel={() => cancelMutation.mutate()}
          hasSignature={Boolean(runbook.trigger_signature)}
        />
      )}

      {children}
    </div>
  );
}

function AwaitingOutcomeQueue({ runs }: { runs: PastRun[] }) {
  if (runs.length === 0) return null;

  // Runs are durable now, so a finished run whose outcome nobody
  // assessed stays actionable across restarts instead of vanishing
  // with the in-memory store. Without this queue an engineer who
  // navigated away had no route back to the run at all.
  return (
    <div
      style={{
        border: '1px solid var(--nx-warning)',
        borderRadius: 'var(--nx-r)',
        padding: 12,
        marginBottom: 16,
      }}
    >
      <strong>{runs.length} run{runs.length === 1 ? '' : 's'} awaiting an outcome</strong>
      <p style={{ fontSize: 12, color: 'var(--nx-muted)', margin: '4px 0 8px' }}>
        The AI assistant only learns from runs whose outcome was recorded. Reopen a run below to
        say whether it actually fixed the issue.
      </p>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
        {runs.map((r) => (
          <li key={r.run_id} style={{ marginBottom: 6 }}>
            <strong>{r.runbook_id}</strong> · {r.site} · {new Date(r.created_at_ms).toLocaleString()}{' '}
            <span style={{ color: 'var(--nx-muted)' }}>
              ({r.status}
              {r.requested_by_email ? `, by ${r.requested_by_email}` : ''})
            </span>
            <div style={{ marginTop: 4 }}>
              <OutcomeRecorder runId={r.run_id} hasSignature={Boolean(r.failure_signature)} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ActPane() {
  const runbooksQuery = useQuery({
    queryKey: ['support', 'runbooks'],
    queryFn: () => listSupportRunbooks(),
  });
  // Hoisted out of `AwaitingOutcomeQueue` so the pinned card can know
  // when the block above it has stopped changing height -- that block
  // is the whole reason the scroll used to miss.
  const awaitingQuery = useQuery({
    queryKey: ['support', 'runbook-runs', 'awaiting'],
    queryFn: () => listRunbookRuns({ awaiting_outcome: true }),
  });
  // Handed over from Pane 2's "Act on this issue" button. `site`
  // pre-selects the run target so the engineer doesn't re-pick the
  // site they were just looking at; `issue` is carried purely so it
  // can be woven into the default reason text -- the audit trail is
  // much more useful when the reason names the issue that prompted
  // the run than when it says "fixing it".
  const [params] = useSearchParams();
  const fromSite = params.get('site') ?? '';
  const fromIssue = params.get('issue') ?? '';
  const fromSignature = params.get('signature') ?? '';
  // `runbook` is the third half of that handoff: Diagnose now offers a
  // "Run →" on every suggested runbook and every AI candidate, and
  // that one names WHICH runbook. Without it the engineer landed at
  // the top of the whole catalogue and had to find it again by title.
  const fromRunbook = params.get('runbook') ?? '';

  // ONE SITE FOR THE WHOLE PANE, hoisted out of the individual cards.
  // It used to live inside each card's confirm form, which meant the
  // answer to "can this site run this?" could not be shown until after
  // the engineer had clicked into a card -- exactly the wrong order,
  // since the point of showing it is to stop the click. It also meant
  // switching sites was N selects instead of one.
  const [site, setSite] = useState(fromSite);
  const sitesQuery = useQuery({ queryKey: ['support', 'sites'], queryFn: listSupportSites });
  // The catalogue is site-independent, so this is a SECOND read of the
  // same endpoint purely for the per-site verdicts. One query for the
  // whole pane, keyed on the site, so switching sites is one request
  // and react-query serves every card from it.
  const availabilityQuery = useQuery({
    queryKey: ['support', 'runbooks', 'availability', site],
    queryFn: () => listSupportRunbooks(site),
    enabled: Boolean(site),
  });
  const availabilityById = new Map(
    (availabilityQuery.data?.runbooks ?? []).map((r) => [r.id, r] as const),
  );
  const siteLabel = (sitesQuery.data ?? []).find((s) => s.site === site)?.label ?? site;

  if (runbooksQuery.isLoading) return <SkeletonLoader shape="card-block" count={3} />;
  if (runbooksQuery.isError) {
    return (
      <EmptyState
        title="Could not load runbook catalogue"
        description={runbooksQuery.error instanceof Error ? runbooksQuery.error.message : 'Unknown error'}
      />
    );
  }

  const { runbooks, note } = runbooksQuery.data!;
  // Stable sort (ES2019+), so the catalogue's own order survives
  // underneath the one card that was asked for. An unknown
  // `?runbook=` id simply pins nothing rather than erroring -- the
  // catalogue changes, old links should still open.
  const pinnedExists = runbooks.some((r) => r.id === fromRunbook);
  const ordered = fromRunbook
    ? [...runbooks].sort(
        (a, b) => Number(b.id === fromRunbook) - Number(a.id === fromRunbook),
      )
    : runbooks;

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Act — runbook catalogue</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <label htmlFor="act-target-site">Target site</label>
        <select
          id="act-target-site"
          aria-label="Target site"
          value={site}
          onChange={(e) => setSite(e.target.value)}
        >
          <option value="">Select a site…</option>
          {(sitesQuery.data ?? []).map((s) => (
            <option key={s.site} value={s.site}>
              {s.label}
            </option>
          ))}
        </select>
        {!site && (
          <span style={{ color: 'var(--nx-muted)', fontSize: 13 }}>
            Choose a site to see which runbooks it can actually run.
          </span>
        )}
        {availabilityQuery.isError && (
          <span style={{ color: 'var(--nx-warning)', fontSize: 13 }}>
            Could not check this site — the buttons below are not gated.
          </span>
        )}
      </div>
      <AwaitingOutcomeQueue runs={awaitingQuery.data ?? []} />
      {note && <p style={{ color: 'var(--nx-warning)' }}>{note}</p>}
      {fromIssue && (
        <p style={{ color: 'var(--nx-cyan)', fontSize: 13 }}>
          Arrived from issue <code>{fromIssue}</code>
          {fromSite ? ` (site ${fromSite})` : ''} — site and reason are pre-filled.
          {fromRunbook && pinnedExists ? ` Showing ${fromRunbook} first.` : ''}
        </p>
      )}
      {fromRunbook && !pinnedExists && (
        <p style={{ color: 'var(--nx-warning)', fontSize: 13 }}>
          Runbook <code>{fromRunbook}</code> is not in this catalogue — it may have been renamed or
          is unavailable to this api-server. The full catalogue is below.
        </p>
      )}
      {runbooks.length === 0 ? (
        <EmptyState title="No runbooks" description="The catalogue is empty." />
      ) : (
        ordered.map((r) => (
          <RunbookCard
            key={r.id}
            runbook={r}
            site={site}
            siteLabel={siteLabel}
            availability={availabilityById.get(r.id)}
            availabilityPending={Boolean(site) && availabilityQuery.isPending}
            fromIssue={fromIssue}
            fromSignature={fromSignature}
            pinned={r.id === fromRunbook}
            layoutSettled={!awaitingQuery.isPending}
          >
            {/* Renders nothing at all for anyone without platform_admin --
                see RetireOrphanedTask.tsx on why that is the whole
                role rule rather than a disabled button. */}
            {r.id === ORPHANED_TASK_RUNBOOK_ID ? (
              <>
                <RetireOrphanedTask site={site} siteLabel={siteLabel} />
                {/* BESIDE Retire, because this is where an engineer
                    already is when Retire refuses. On 2026-09-15 that
                    refusal came twice with nowhere to go. */}
                <EndHeldTask site={site} siteLabel={siteLabel} />
              </>
            ) : null}
          </RunbookCard>
        ))
      )}
    </div>
  );
}
